import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { describeError } from '../errors.js';
import type { AdminHttpClient } from '../http.js';
import type {
  DesktopModelView,
  TenantLoginDefaultModelView,
  ToastMessage
} from '../types.js';
import { renderModelSpec } from './model-capability-summary.js';
import { modelCustomerLabel } from './model-customer-label.js';

export type TenantLoginDefaultModelState =
  | 'loading'
  | 'error'
  | 'empty'
  | 'stale'
  | 'ready';

export function tenantLoginDefaultModelState(
  view: TenantLoginDefaultModelView | undefined,
  loading: boolean,
  failed: boolean
): TenantLoginDefaultModelState {
  if (failed) return 'error';
  if (loading) return 'loading';
  if (!view) return 'error';
  if (view.candidates.length === 0)
    return view.loginDefaultModelId ? 'stale' : 'empty';
  if (
    view.loginDefaultModelId &&
    !view.candidates.some((model) => model.id === view.loginDefaultModelId)
  )
    return 'stale';
  return 'ready';
}

export function canSaveTenantLoginDefaultModel(
  view: TenantLoginDefaultModelView | undefined,
  selectedModelId: string,
  busy: boolean
): boolean {
  if (!view || busy || selectedModelId === (view.loginDefaultModelId ?? ''))
    return false;
  return view.candidates.some((model) => model.id === selectedModelId);
}

function displayName(model: DesktopModelView): string {
  return modelCustomerLabel(model);
}

export function TenantLoginDefaultModelScreen({
  http,
  tenantId,
  onToast
}: {
  http: AdminHttpClient;
  tenantId: string;
  onToast: (toast: ToastMessage) => void;
}) {
  const { t } = useTranslation();
  const translateError = useCallback(
    (error: unknown) =>
      describeError(error, (key, options) =>
        String(options ? t(key, options) : t(key))
      ),
    [t]
  );
  const requestId = useRef(0);
  const [view, setView] = useState<TenantLoginDefaultModelView>();
  const [selectedModelId, setSelectedModelId] = useState('');
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  const load = useCallback(async () => {
    if (!tenantId) return;
    const currentRequest = ++requestId.current;
    setLoading(true);
    setFailed(false);
    setBusy(false);
    setStatusMessage('');
    try {
      const next = await http.getTenantLoginDefaultModel();
      if (currentRequest !== requestId.current) return;
      if (next.tenantId !== tenantId) {
        setView(undefined);
        setFailed(true);
        return;
      }
      setView(next);
      setSelectedModelId(next.loginDefaultModelId ?? '');
    } catch (error) {
      if (currentRequest !== requestId.current) return;
      setFailed(true);
      onToast({ tone: 'error', message: translateError(error) });
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
  }, [http, onToast, tenantId, translateError]);

  useEffect(() => {
    void load();
    return () => {
      requestId.current += 1;
    };
  }, [load]);

  const activeView = view?.tenantId === tenantId ? view : undefined;
  const candidateIds = useMemo(
    () => new Set(activeView?.candidates.map((model) => model.id) ?? []),
    [activeView]
  );
  const state = tenantLoginDefaultModelState(
    activeView,
    loading || (view !== undefined && activeView === undefined),
    failed
  );

  const update = async (loginDefaultModelId: string | null) => {
    if (busy) return;
    if (
      loginDefaultModelId !== null &&
      !candidateIds.has(loginDefaultModelId)
    )
      return;
    const currentRequest = ++requestId.current;
    setBusy(true);
    try {
      const next = await http.updateTenantLoginDefaultModel(
        loginDefaultModelId
      );
      if (currentRequest !== requestId.current || next.tenantId !== tenantId)
        return;
      setView(next);
      setSelectedModelId(next.loginDefaultModelId ?? '');
      const message = loginDefaultModelId
        ? t('tenantLoginDefaultModel.saved')
        : t('tenantLoginDefaultModel.cleared');
      setStatusMessage(message);
      onToast({ tone: 'success', message });
    } catch (error) {
      if (currentRequest !== requestId.current) return;
      onToast({ tone: 'error', message: translateError(error) });
    } finally {
      if (currentRequest === requestId.current) setBusy(false);
    }
  };

  return (
    <section className="screen-stack" data-state={state}>
      <div className="section-heading">
        <div>
          <h2>{t('tenantLoginDefaultModel.title')}</h2>
          <p>{t('tenantLoginDefaultModel.help')}</p>
          <p>{t('recommendedModels.loginDefaultHelp')}</p>
        </div>
      </div>

      <section className="panel content-stack">
        {state === 'loading' ? (
          <p role="status">{t('tenantLoginDefaultModel.loading')}</p>
        ) : null}
        {state === 'error' ? (
          <div className="content-stack" role="alert">
            <p>{t('tenantLoginDefaultModel.loadError')}</p>
            <button type="button" className="secondary" onClick={() => void load()}>
              {t('tenantLoginDefaultModel.retry')}
            </button>
          </div>
        ) : null}
        {state === 'empty' ? (
          <p>{t('tenantLoginDefaultModel.empty')}</p>
        ) : null}
        {state === 'stale' ? (
          <div className="context-banner compact" role="status">
            {t('tenantLoginDefaultModel.stale', {
              modelId: activeView?.loginDefaultModelId
            })}
          </div>
        ) : null}

        {activeView && (state === 'ready' || state === 'stale') ? (
          <fieldset className="form-grid">
            <legend>{t('tenantLoginDefaultModel.candidates')}</legend>
            {activeView.candidates.map((model) => (
              <label className="panel" key={model.id}>
                <input
                  type="radio"
                  name="tenant-login-default-model"
                  value={model.id}
                  checked={selectedModelId === model.id}
                  disabled={busy}
                  onChange={() => setSelectedModelId(model.id)}
                />
                <span className="content-stack">
                  <strong title={displayName(model)}>{displayName(model)}</strong>
                  <span className="muted" title={model.id}>
                    {model.customerProviderName ?? model.provider} · {model.id}
                  </span>
                  <span className="muted">{renderModelSpec(t, model)}</span>
                  <span className="badge-group capability-badges">
                    {model.capabilities.reasoning ? (
                      <span className="badge active">{t('tenantLoginDefaultModel.reasoning')}</span>
                    ) : null}
                    {model.capabilities.vision ? (
                      <span className="badge active">{t('tenantLoginDefaultModel.vision')}</span>
                    ) : null}
                    {model.capabilities.toolUse ? (
                      <span className="badge active">{t('tenantLoginDefaultModel.toolUse')}</span>
                    ) : null}
                  </span>
                </span>
              </label>
            ))}
            <div className="button-row">
              <button
                type="button"
                disabled={!canSaveTenantLoginDefaultModel(activeView, selectedModelId, busy)}
                onClick={() => void update(selectedModelId)}
              >
                {busy ? t('tenantLoginDefaultModel.saving') : t('tenantLoginDefaultModel.save')}
              </button>
              <button
                type="button"
                className="secondary"
                disabled={busy || !activeView.loginDefaultModelId}
                onClick={() => void update(null)}
              >
                {t('tenantLoginDefaultModel.clear')}
              </button>
            </div>
          </fieldset>
        ) : null}
      </section>

      <section className="panel content-stack">
        <p>{t('tenantLoginDefaultModel.localSelectionNotice')}</p>
        <p>{t('tenantLoginDefaultModel.unavailableNotice')}</p>
        <p>{t('tenantLoginDefaultModel.activationNotice')}</p>
      </section>
      <p className="muted" aria-live="polite">{statusMessage}</p>
    </section>
  );
}
